import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Users,
  DollarSign,
  TrendingUp,
  Zap,
  Search,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  Download,
  Mail,
  Send,
  Trophy,
  Target,
  CalendarDays,
  MessageSquareText,
  Flame,
  BarChart3,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.35,
      ease: [0.2, 0.8, 0.2, 1],
    },
  }),
};

const CAPITAL_FILTERS = [
  "Below $100",
  "$100 – $500",
  "$500 – $1,000",
  "$1,000 – $5,000",
  "$5,000 and above",
];

const EXPERIENCE_FILTERS = ["Beginner", "Intermediate", "Advanced"];

const READINESS_FILTERS = [
  "Immediately (today)",
  "Within 1–3 days",
  "Within this week",
  "Just exploring",
];

const CHART_COLORS = [
  "hsl(350, 58%, 42%)",
  "hsl(350, 40%, 52%)",
  "hsl(240, 4%, 35%)",
  "hsl(240, 3%, 50%)",
  "hsl(20, 60%, 50%)",
];

const PIE_COLORS = [
  "#a3344a",
  "#c0566c",
  "#4a4a5a",
  "#6b6b7a",
  "#cc7733",
];

function toChartData(dist) {
  if (!dist) return [];
  return Object.entries(dist).map(([name, value]) => ({ name, value }));
}

function shortLabel(label) {
  const map = {
    "More than 6 months": "6+ months",
    "Less than 1 month": "< 1 month",
    "1 – 3 months": "1-3 months",
    "3 – 6 months": "3-6 months",
    "Immediately (today)": "Immediately",
    "Within 1–3 days": "1-3 days",
    "Within this week": "This week",
    "Just exploring": "Exploring",
    "Profitable and satisfied": "Profitable",
    "Profitable but I want more": "Want more",
    "Still learning the process": "Learning",
    "Never tried": "Never tried",
    "$5,000 and above": "$5K+",
    "$1,000 – $5,000": "$1K-5K",
    "$500 – $1,000": "$500-1K",
    "$100 – $500": "$100-500",
    "Below $100": "< $100",
    "Telegram Channel": "Telegram",
    "Friend / Referral": "Friend",
    "Social Media": "Social",
    "Broker Leaderboard": "Leaderboard",
  };
  return map[label] || label;
}

const CustomBarLabel = ({ x, y, width, value }) => {
  if (!value) return null;
  return (
    <text x={x + width / 2} y={y - 6} fill="hsl(30,8%,75%)" textAnchor="middle" fontSize={11} fontWeight={600}>
      {value}
    </text>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-[hsl(240,4%,20%)] bg-[hsl(240,5%,10%)] px-3 py-2 shadow-lg">
        <p className="text-xs text-[hsl(30,8%,85%)] font-medium">{payload[0].name}</p>
        <p className="text-sm font-semibold text-white font-mono">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function capitalBadgeColor(capital) {
  if (capital?.includes("5,000 and above")) return "bg-[hsl(350,58%,42%,0.12)] text-[hsl(350,58%,52%)] border-[hsl(350,58%,42%,0.25)]";
  if (capital?.includes("1,000")) return "bg-[hsl(240,4%,16%)] text-[hsl(30,8%,80%)] border-[hsl(240,4%,22%)]";
  return "bg-muted text-muted-foreground border-border/50";
}

function experienceBadgeColor(exp) {
  if (exp === "Advanced") return "bg-[hsl(350,58%,42%,0.12)] text-[hsl(350,58%,52%)] border-[hsl(350,58%,42%,0.25)]";
  if (exp === "Intermediate") return "bg-[hsl(240,4%,16%)] text-[hsl(30,8%,80%)] border-[hsl(240,4%,22%)]";
  return "bg-muted text-muted-foreground border-border/50";
}

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "LDC2025";

function generatePDF(applications, stats) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 16;
  let y = 20;

  // ─── Header ───
  doc.setFillColor(15, 15, 18);
  doc.rect(0, 0, pageWidth, 45, "F");
  doc.setTextColor(163, 52, 74);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text("THE LAST DANCE CAPITAL", margin, 22);
  doc.setTextColor(180, 180, 180);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Application Report — Confidential", margin, 30);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`, margin, 36);
  y = 55;

  // ─── KPI Summary ───
  doc.setTextColor(163, 52, 74);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("SUMMARY", margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  const kpis = [
    ["Total Applications", stats?.total_submissions || 0],
    ["$5,000+ Capital", stats?.capital_distribution?.["$5,000 and above"] || 0],
    ["6+ Months Followers", stats?.experience_distribution?.["More than 6 months"] || 0],
    ["Ready Immediately", stats?.readiness_distribution?.["Immediately (today)"] || 0],
    ["All Commitments Checked", stats?.commitment_all_checked || 0],
  ];
  kpis.forEach(([label, val]) => {
    doc.setFont("helvetica", "normal");
    doc.text(label + ":", margin, y);
    doc.setFont("helvetica", "bold");
    doc.text(String(val), margin + 60, y);
    y += 6;
  });
  y += 4;

  // ─── Financial Projection ───
  doc.setTextColor(163, 52, 74);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("FINANCIAL PROJECTION", margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const financials = [
    ["Total Potential Capital", `$${(stats?.total_potential_capital || 0).toLocaleString()}`],
    ["Avg Capital / Client", `$${(stats?.avg_capital_per_client || 0).toLocaleString()}`],
    ["Est. Daily Profit (5%)", `$${(stats?.est_daily_low || 0).toLocaleString()}`],
    ["Est. Daily Profit (10%)", `$${(stats?.est_daily_high || 0).toLocaleString()}`],
    ["Est. Monthly Profit (5%)", `$${(stats?.est_monthly_low || 0).toLocaleString()}`],
    ["Est. Monthly Profit (10%)", `$${(stats?.est_monthly_high || 0).toLocaleString()}`],
    ["Total Potential Clients", String(stats?.total_submissions || 0)],
    ["Ready Immediately", String(stats?.ready_immediately_count || 0)],
  ];
  financials.forEach(([label, val]) => {
    doc.setFont("helvetica", "normal");
    doc.text(label + ":", margin, y);
    doc.setFont("helvetica", "bold");
    doc.text(val, margin + 70, y);
    y += 6;
  });
  y += 4;

  // ─── Top 10 Country Capital Ranking ───
  if (stats?.country_capital_ranking && stats.country_capital_ranking.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setTextColor(163, 52, 74);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("TOP 10 COUNTRY — HIGHEST CAPITAL", margin, y);
    y += 6;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    stats.country_capital_ranking.forEach((item, idx) => {
      if (y > 275) { doc.addPage(); y = 20; }
      const rank = idx < 3 ? ["#1 ", "#2 ", "#3 "][idx] : `#${idx + 1} `;
      doc.text(`${rank}${item.country} — $${item.total_capital.toLocaleString()} (${item.client_count} clients, avg $${item.avg_capital.toLocaleString()})`, margin, y);
      y += 5;
    });
    y += 4;
  }

  // ─── AD INTELLIGENCE ───
  doc.addPage(); y = 20;
  doc.setTextColor(163, 52, 74);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("AD INTELLIGENCE METRICS", margin, y);
  y += 10;

  // Referral ROI
  if (stats?.referral_capital_ranking && stats.referral_capital_ranking.length > 0) {
    doc.setTextColor(163, 52, 74);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("REFERRAL ROI — CHANNEL PERFORMANCE", margin, y);
    y += 6;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    stats.referral_capital_ranking.forEach((item, idx) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.text(`#${idx + 1} ${item.source} — $${item.total_capital.toLocaleString()} (${item.client_count} leads, avg $${item.avg_capital.toLocaleString()})`, margin, y);
      y += 5;
    });
    y += 6;
  }

  // Hot Leads
  if (stats?.hot_leads_summary) {
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setTextColor(163, 52, 74);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("HOT LEADS SCORE", margin, y);
    y += 6;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const hl = stats.hot_leads_summary;
    doc.text(`Tier A (Premium — $5K+ Ready Full Commit): ${hl.tier_a} (${hl.total > 0 ? Math.round((hl.tier_a / hl.total) * 100) : 0}%)`, margin, y); y += 5;
    doc.text(`Tier B (Qualified — Partial Match): ${hl.tier_b} (${hl.total > 0 ? Math.round((hl.tier_b / hl.total) * 100) : 0}%)`, margin, y); y += 5;
    doc.text(`Tier C (Needs Nurturing): ${hl.tier_c} (${hl.total > 0 ? Math.round((hl.tier_c / hl.total) * 100) : 0}%)`, margin, y); y += 8;
  }

  // Signup Trend
  if (stats?.signup_trend && stats.signup_trend.length > 0) {
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setTextColor(163, 52, 74);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("SIGNUP TREND", margin, y);
    y += 6;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    stats.signup_trend.forEach(d => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.text(`${d.date}: ${d.count} signups — $${d.capital.toLocaleString()} capital`, margin, y);
      y += 5;
    });
    y += 6;
  }

  // Top Challenges
  if (stats?.top_challenges && stats.top_challenges.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setTextColor(163, 52, 74);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("TOP CHALLENGES — AD COPY INSIGHTS", margin, y);
    y += 6;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    stats.top_challenges.forEach((item, idx) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.text(`#${idx + 1} "${item.challenge}" (${item.count}x)`, margin, y);
      y += 5;
    });
    y += 4;
  }

  // ─── Distribution tables ───
  if (stats) {
    const distributions = [
      ["Capital Distribution", stats.capital_distribution],
      ["Copytrade Duration", stats.experience_distribution],
      ["Copytrade Results", stats.signals_distribution],
      ["Readiness", stats.readiness_distribution],
      ["Referral Source", stats.referral_distribution],
      ["Country", stats.country_distribution],
    ];
    distributions.forEach(([title, dist]) => {
      if (!dist || Object.keys(dist).length === 0) return;
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setTextColor(163, 52, 74);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(title, margin, y);
      y += 5;
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      Object.entries(dist).forEach(([k, v]) => {
        doc.text(`${k}: ${v}`, margin + 4, y);
        y += 5;
      });
      y += 3;
    });
  }
  y += 4;

  // ─── Applications Table ───
  if (y > 230) { doc.addPage(); y = 20; }
  doc.setTextColor(163, 52, 74);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("ALL APPLICATIONS", margin, y);
  y += 6;

  const tableHead = [["#", "Name", "Country", "Email", "Telegram", "Capital", "Duration", "Results", "Readiness", "Date"]];
  const tableBody = applications.map((a, i) => [
    i + 1,
    a.full_name || "",
    a.country || "",
    a.email || "",
    a.telegram_username || "",
    shortLabel(a.trading_capital) || "",
    shortLabel(a.trading_experience) || "",
    shortLabel(a.signals_experience) || "",
    shortLabel(a.readiness_to_start) || "",
    a.created_at ? new Date(a.created_at).toLocaleDateString("en-GB") : "",
  ]);

  autoTable(doc, {
    startY: y,
    head: tableHead,
    body: tableBody,
    theme: "grid",
    headStyles: { fillColor: [163, 52, 74], textColor: 255, fontSize: 7, fontStyle: "bold" },
    bodyStyles: { fontSize: 7, textColor: [50, 50, 50] },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: margin, right: margin },
    styles: { cellPadding: 2, overflow: "linebreak" },
    columnStyles: { 0: { cellWidth: 8 }, 3: { cellWidth: 30 }, 4: { cellWidth: 22 } },
  });

  y = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 10 : y + 20;

  // ─── Detail per applicant ───
  applications.forEach((a, i) => {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setDrawColor(163, 52, 74);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    doc.setTextColor(163, 52, 74);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`${i + 1}. ${a.full_name || "Unknown"}`, margin, y);
    y += 6;

    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    const details = [
      ["Email", a.email],
      ["Telegram", a.telegram_username],
      ["Country", a.country],
      ["Capital", a.trading_capital],
      ["Investment Target", a.desired_capital ? `$${Number(a.desired_capital).toLocaleString()}` : "—"],
      ["Projected Monthly (5%-10%)", a.desired_capital ? `$${(a.desired_capital * 0.05 * 22).toFixed(0)} - $${(a.desired_capital * 0.10 * 22).toFixed(0)}` : "—"],
      ["Duration", a.trading_experience],
      ["Copytrade Results", a.signals_experience],
      ["Referral", a.referral_source],
      ["Commitment", `IB: ${a.commitment_open_ib ? "Yes" : "No"} | Rules: ${a.commitment_risk_rules ? "Yes" : "No"} | 1 Month: ${a.commitment_one_month ? "Yes" : "No"} | AI Tools: ${a.accept_ai_tools ? "Yes" : "No"}`],
      ["Readiness", a.readiness_to_start],
    ];

    details.forEach(([label, val]) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text(label + ":", margin, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      doc.text(String(val || "—"), margin + 35, y);
      y += 5;
    });

    // Why they want in (longer text)
    if (a.biggest_challenge) {
      if (y > 265) { doc.addPage(); y = 20; }
      y += 2;
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text("Why They Want In:", margin, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(a.biggest_challenge, pageWidth - margin * 2 - 4);
      doc.text(lines, margin + 2, y);
      y += lines.length * 4 + 4;
    }
    y += 6;
  });

  // ─── Footer ───
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(7);
    doc.text("The Last Dance Capital — Confidential — Powered by UNDERADS", margin, 290);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 20, 290);
  }

  doc.save("LastDanceCapital_Report.pdf");
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [capitalFilter, setCapitalFilter] = useState("all");
  const [experienceFilter, setExperienceFilter] = useState("all");
  const [readinessFilter, setReadinessFilter] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  // Check sessionStorage for auth
  useEffect(() => {
    if (sessionStorage.getItem("ldc_admin") === "true") {
      setAuthenticated(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (capitalFilter !== "all") params.capital = capitalFilter;
      if (experienceFilter !== "all") params.experience = experienceFilter;
      if (readinessFilter !== "all") params.readiness = readinessFilter;

      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/applications`, { params }),
        axios.get(`${API}/applications/stats`),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      // Error handled silently in production
    } finally {
      setLoading(false);
    }
  }, [search, capitalFilter, experienceFilter, readinessFilter, authenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setAuthenticated(true);
      sessionStorage.setItem("ldc_admin", "true");
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  // Password gate
  if (!authenticated) {
    return (
      <div className="page-bg">
        <div className="hero-sheen" />
        <div className="fixed inset-0 z-10 flex items-center justify-center px-4">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(350,58%,42%,0.1)] mb-4">
                <Lock className="h-6 w-6 text-[hsl(350,58%,48%)]" />
              </div>
              <h1 className="font-display text-2xl text-foreground mb-1">Admin Access</h1>
              <p className="text-sm text-muted-foreground">Enter password to continue</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                className="bg-background/40 border-border/80 text-center text-lg tracking-widest h-12"
                autoFocus
              />
              {passwordError && (
                <p className="text-xs text-[hsl(350,58%,52%)] text-center">Wrong password.</p>
              )}
              <Button
                type="submit"
                className="w-full rounded-xl bg-[hsl(350,58%,42%)] hover:bg-[hsl(350,58%,38%)] text-white h-11"
              >
                Enter
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-bg">
      <div className="hero-sheen" />

      {/* Powered by footer */}
      <div className="fixed bottom-3 left-0 right-0 z-40 text-center pointer-events-none">
        <span className="text-[0.55rem] font-mono uppercase tracking-[0.15em] text-[hsl(240,3%,52%)]">
          Powered by UNDERADS
        </span>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={0}
          variants={fadeIn}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="rounded-xl hover:bg-background/30 -ml-2"
                data-testid="back-to-form-button"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Form
              </Button>
            </div>
            <h1
              className="font-display text-3xl sm:text-4xl font-normal tracking-[-0.02em] text-foreground"
              data-testid="admin-title"
            >
              The Last Dance Capital
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Application Dashboard — Review trader screening submissions
            </p>
          </div>
          <div className="flex gap-2 self-start">
            <Button
              variant="secondary"
              onClick={() => {
                const csvHeader = "Name,Country,Telegram,Email,Capital,Duration,Results,Referral,Investment Target,Why,Commitment IB,Commitment Rules,Commitment 1 Month,AI Tools,Readiness,Date\n";
                const csvRows = applications.map(a => 
                  [a.full_name, a.country, a.telegram_username, a.email, a.trading_capital, a.trading_experience, a.signals_experience, a.referral_source, `$${a.desired_capital}@${a.daily_target_percent}%`, `"${(a.biggest_challenge||'').replace(/"/g,'""')}"`, a.commitment_open_ib, a.commitment_risk_rules, a.commitment_one_month, a.accept_ai_tools, a.readiness_to_start, a.created_at].join(",")
                ).join("\n");
                const blob = new Blob([csvHeader + csvRows], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "applications.csv";
                link.click();
                URL.revokeObjectURL(url);
              }}
              className="rounded-xl bg-secondary/70 hover:bg-secondary"
              data-testid="admin-export-button"
              disabled={applications.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="secondary"
              onClick={() => generatePDF(applications, stats)}
              className="rounded-xl bg-secondary/70 hover:bg-secondary"
              data-testid="admin-pdf-button"
              disabled={applications.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              PDF Report
            </Button>
            <Button
              variant="secondary"
              onClick={fetchData}
              className="rounded-xl bg-secondary/70 hover:bg-secondary"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </motion.div>

        {/* KPI Cards */}
        {stats && (
          <motion.div
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeIn}
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8"
          >
            <Card className="bg-card/70 border-border/70" data-testid="admin-kpi-total-submissions">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      Total Applications
                    </p>
                    <p className="text-3xl font-semibold text-foreground mt-1 font-mono">
                      {stats.total_submissions}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-[hsl(350,58%,42%,0.1)] flex items-center justify-center">
                    <Users className="h-5 w-5 text-[hsl(350,58%,48%)]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/70 border-border/70" data-testid="admin-kpi-top-capital-tier">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      $5,000+ Capital
                    </p>
                    <p className="text-3xl font-semibold text-foreground mt-1 font-mono">
                      {stats.capital_distribution["$5,000 and above"] || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-[hsl(350,58%,42%,0.1)] flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-[hsl(350,58%,48%)]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/70 border-border/70" data-testid="admin-kpi-experience-split">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      6+ Months Followers
                    </p>
                    <p className="text-3xl font-semibold text-foreground mt-1 font-mono">
                      {stats.experience_distribution["More than 6 months"] || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-[hsl(240,4%,14%)] flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-[hsl(240,3%,60%)]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/70 border-border/70" data-testid="admin-kpi-ready-immediately">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      Ready Now
                    </p>
                    <p className="text-3xl font-semibold text-foreground mt-1 font-mono">
                      {stats.readiness_distribution["Immediately (today)"] || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-[hsl(240,4%,14%)] flex items-center justify-center">
                    <Zap className="h-5 w-5 text-[hsl(240,3%,60%)]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Financial Summary */}
        {stats && stats.total_submissions > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            custom={1.2}
            variants={fadeIn}
            className="mb-8"
          >
            <Card className="bg-[hsl(350,58%,42%,0.04)] border-[hsl(350,58%,42%,0.15)]">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="h-4 w-4 text-[hsl(350,58%,48%)]" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-[hsl(350,58%,48%)]">
                    Financial Projection
                  </h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                  <div>
                    <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Total Potential Capital</p>
                    <p className="text-2xl sm:text-3xl font-bold text-foreground font-mono">
                      ${stats.total_potential_capital?.toLocaleString() || "0"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Avg Capital / Client</p>
                    <p className="text-2xl sm:text-3xl font-bold text-foreground font-mono">
                      ${stats.avg_capital_per_client?.toLocaleString() || "0"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Est. Daily Profit (5%-10%)</p>
                    <p className="text-xl sm:text-2xl font-bold text-foreground font-mono">
                      ${stats.est_daily_low?.toLocaleString() || "0"}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      to ${stats.est_daily_high?.toLocaleString() || "0"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Est. Monthly Profit (22 days)</p>
                    <p className="text-xl sm:text-2xl font-bold text-[hsl(350,58%,48%)] font-mono">
                      ${stats.est_monthly_low?.toLocaleString() || "0"}
                    </p>
                    <p className="text-xs text-[hsl(350,58%,48%,0.7)] font-mono mt-0.5">
                      to ${stats.est_monthly_high?.toLocaleString() || "0"}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-[hsl(240,4%,16%)] flex flex-wrap gap-4 sm:gap-8">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Total Clients:</span>
                    <span className="text-sm font-semibold text-foreground font-mono">{stats.total_submissions}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Ready Now:</span>
                    <span className="text-sm font-semibold text-foreground font-mono">{stats.ready_immediately_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Full Commitment:</span>
                    <span className="text-sm font-semibold text-foreground font-mono">{stats.commitment_all_checked}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Charts */}
        {stats && stats.total_submissions > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            custom={1.5}
            variants={fadeIn}
            className="grid gap-4 md:grid-cols-2 mb-8"
          >
            {/* Pie Chart - Capital Distribution */}
            <Card className="bg-card/70 border-border/70" data-testid="chart-capital-pie">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Capital Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={toChartData(stats.capital_distribution)}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {toChartData(stats.capital_distribution).map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span className="text-[0.65rem] text-[hsl(240,3%,55%)]">{shortLabel(value)}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Bar Chart - Copytrade Duration */}
            <Card className="bg-card/70 border-border/70" data-testid="chart-experience-bar">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Copytrade Duration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={toChartData(stats.experience_distribution)} barSize={36}>
                    <XAxis
                      dataKey="name"
                      tick={({ x, y, payload }) => (
                        <text x={x} y={y + 12} textAnchor="middle" fill="hsl(240,3%,45%)" fontSize={10}>
                          {shortLabel(payload.value)}
                        </text>
                      )}
                      tickLine={false}
                      axisLine={{ stroke: "hsl(240,4%,18%)" }}
                      interval={0}
                      height={40}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "hsl(240,3%,45%)" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsla(350,58%,42%,0.06)" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} label={<CustomBarLabel />}>
                      {toChartData(stats.experience_distribution).map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Pie Chart - Copytrade Results */}
            <Card className="bg-card/70 border-border/70" data-testid="chart-signals-pie">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Copytrade Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={toChartData(stats.signals_distribution)}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {toChartData(stats.signals_distribution).map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span className="text-[0.65rem] text-[hsl(240,3%,55%)]">{shortLabel(value)}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Bar Chart - Readiness */}
            <Card className="bg-card/70 border-border/70" data-testid="chart-readiness-bar">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Readiness to Start
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={toChartData(stats.readiness_distribution)} barSize={36}>
                    <XAxis
                      dataKey="name"
                      tick={({ x, y, payload }) => (
                        <text x={x} y={y + 12} textAnchor="middle" fill="hsl(240,3%,45%)" fontSize={10}>
                          {shortLabel(payload.value)}
                        </text>
                      )}
                      tickLine={false}
                      axisLine={{ stroke: "hsl(240,4%,18%)" }}
                      interval={0}
                      height={40}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "hsl(240,3%,45%)" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsla(350,58%,42%,0.06)" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} label={<CustomBarLabel />}>
                      {toChartData(stats.readiness_distribution).map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Pie Chart - Referral Source */}
            <Card className="bg-card/70 border-border/70" data-testid="chart-referral-pie">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Referral Source
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={toChartData(stats.referral_distribution || {})}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {toChartData(stats.referral_distribution || {}).map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span className="text-[0.65rem] text-[hsl(240,3%,55%)]">{shortLabel(value)}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Country Distribution - Top 20 for ads targeting */}
            {stats.country_distribution && Object.keys(stats.country_distribution).length > 0 && (
            <Card className="bg-card/70 border-border/70 md:col-span-2" data-testid="chart-country-bar">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Top Countries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={(() => {
                  const count = Math.min(Object.keys(stats.country_distribution).filter(k => k && k !== "" && k !== "None").length, 20);
                  return Math.max((count + 1) * 32, 200);
                })()}>
                  <BarChart data={(() => {
                    const sorted = Object.entries(stats.country_distribution)
                      .filter(([k]) => k && k !== "" && k !== "None")
                      .sort((a, b) => b[1] - a[1]);
                    const top = sorted.slice(0, 20).map(([name, value]) => ({ name, value }));
                    const othersCount = sorted.slice(20).reduce((sum, [, v]) => sum + v, 0);
                    if (othersCount > 0) top.push({ name: "Others", value: othersCount });
                    return top;
                  })()} barSize={20} layout="vertical" margin={{ left: 10, right: 40 }}>
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: "hsl(240,3%,45%)" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "hsl(240,3%,60%)" }}
                      tickLine={false}
                      axisLine={false}
                      width={120}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsla(350,58%,42%,0.06)" }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 11, fill: "hsl(30,8%,75%)", fontWeight: 600 }}>
                      {Array.from({ length: 21 }).map((_, i) => (
                        <Cell key={`country-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            )}
          </motion.div>
        )}

        {/* Top 10 Country Capital Ranking */}
        {stats && stats.country_capital_ranking && stats.country_capital_ranking.length > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            custom={1.8}
            variants={fadeIn}
            className="mb-8"
          >
            <Card className="bg-[hsl(350,58%,42%,0.04)] border-[hsl(350,58%,42%,0.15)]" data-testid="country-capital-ranking">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-[hsl(40,80%,55%)]" />
                  <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[hsl(350,58%,48%)]">
                    Top 10 Country — Highest Capital
                  </CardTitle>
                </div>
                <p className="text-[0.65rem] text-muted-foreground mt-1">Ranked by total deposit. Showing quality markets.</p>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div className="space-y-2">
                  {stats.country_capital_ranking.map((item, idx) => {
                    const maxCapital = stats.country_capital_ranking[0]?.total_capital || 1;
                    const barWidth = (item.total_capital / maxCapital) * 100;
                    const medalColors = [
                      "text-[hsl(40,80%,55%)]",  // Gold
                      "text-[hsl(240,3%,70%)]",   // Silver
                      "text-[hsl(25,60%,45%)]",    // Bronze
                    ];
                    return (
                      <div key={item.country} className="group relative">
                        <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-[hsl(240,4%,12%)] transition-colors duration-150">
                          {/* Rank */}
                          <div className={`w-7 text-center font-mono font-bold text-sm ${idx < 3 ? medalColors[idx] : "text-muted-foreground"}`}>
                            {idx < 3 ? ["🥇","🥈","🥉"][idx] : `#${idx + 1}`}
                          </div>
                          {/* Country Name */}
                          <div className="w-28 sm:w-36 shrink-0">
                            <span className={`text-sm font-semibold ${idx === 0 ? "text-[hsl(40,80%,55%)]" : "text-foreground"}`}>
                              {item.country}
                            </span>
                          </div>
                          {/* Progress Bar */}
                          <div className="flex-1 relative h-7 bg-[hsl(240,4%,10%)] rounded overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 rounded transition-all duration-500"
                              style={{
                                width: `${barWidth}%`,
                                background: idx === 0
                                  ? "linear-gradient(90deg, hsl(40,80%,35%), hsl(40,80%,50%))"
                                  : idx === 1
                                  ? "linear-gradient(90deg, hsl(240,3%,30%), hsl(240,3%,45%))"
                                  : idx === 2
                                  ? "linear-gradient(90deg, hsl(25,40%,28%), hsl(25,50%,40%))"
                                  : "linear-gradient(90deg, hsl(350,58%,25%), hsl(350,58%,38%))"
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-end pr-2">
                              <span className="text-xs font-mono font-bold text-white/90">
                                ${item.total_capital.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          {/* Stats */}
                          <div className="hidden sm:flex items-center gap-3 shrink-0 w-40">
                            <div className="text-center">
                              <p className="text-[0.6rem] uppercase text-muted-foreground">Clients</p>
                              <p className="text-xs font-mono font-semibold text-foreground">{item.client_count}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-[0.6rem] uppercase text-muted-foreground">Avg</p>
                              <p className="text-xs font-mono font-semibold text-foreground">${item.avg_capital.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══════ AD INTELLIGENCE METRICS ═══════ */}
        {stats && stats.total_submissions > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeIn}
          >
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6 mt-2">
              <div className="h-px flex-1 bg-[hsl(350,58%,42%,0.2)]" />
              <div className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-[hsl(350,58%,42%,0.2)] bg-[hsl(350,58%,42%,0.05)]">
                <Target className="h-3.5 w-3.5 text-[hsl(350,58%,48%)]" />
                <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[hsl(350,58%,48%)]">
                  Ad Intelligence
                </span>
              </div>
              <div className="h-px flex-1 bg-[hsl(350,58%,42%,0.2)]" />
            </div>

            <div className="grid gap-4 md:grid-cols-2 mb-6">

              {/* ─── 1) REFERRAL ROI ─── */}
              {stats.referral_capital_ranking && stats.referral_capital_ranking.length > 0 && (
                <Card className="bg-card/70 border-border/70 md:col-span-2" data-testid="referral-roi">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[hsl(145,50%,45%)]" />
                      <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        Referral ROI — Channel Mana Bawa Duit
                      </CardTitle>
                    </div>
                    <p className="text-[0.6rem] text-muted-foreground">Ranked by total capital brought in. Spend more on top channels.</p>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-6">
                    <div className="space-y-1.5">
                      {stats.referral_capital_ranking.map((item, idx) => {
                        const maxCap = stats.referral_capital_ranking[0]?.total_capital || 1;
                        const pct = (item.total_capital / maxCap) * 100;
                        return (
                          <div key={item.source} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[hsl(240,4%,12%)] transition-colors duration-150">
                            <span className={`w-6 text-center font-mono text-xs font-bold ${idx === 0 ? "text-[hsl(145,50%,50%)]" : "text-muted-foreground"}`}>
                              #{idx + 1}
                            </span>
                            <span className="w-32 sm:w-44 text-sm font-medium text-foreground truncate">{item.source}</span>
                            <div className="flex-1 relative h-6 bg-[hsl(240,4%,10%)] rounded overflow-hidden">
                              <div className="absolute inset-y-0 left-0 rounded" style={{
                                width: `${pct}%`,
                                background: idx === 0
                                  ? "linear-gradient(90deg, hsl(145,50%,25%), hsl(145,50%,40%))"
                                  : "linear-gradient(90deg, hsl(350,58%,25%), hsl(350,58%,38%))"
                              }} />
                              <div className="absolute inset-0 flex items-center justify-end pr-2">
                                <span className="text-[0.65rem] font-mono font-bold text-white/90">${item.total_capital.toLocaleString()}</span>
                              </div>
                            </div>
                            <div className="hidden sm:flex gap-3 shrink-0 w-32">
                              <div className="text-center">
                                <p className="text-[0.55rem] uppercase text-muted-foreground">Leads</p>
                                <p className="text-xs font-mono font-semibold">{item.client_count}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[0.55rem] uppercase text-muted-foreground">Avg</p>
                                <p className="text-xs font-mono font-semibold">${item.avg_capital.toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ─── 2) HOT LEADS SCORE ─── */}
              <Card className="bg-card/70 border-border/70" data-testid="hot-leads-score">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Flame className="h-4 w-4 text-[hsl(25,90%,55%)]" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      Hot Leads Score
                    </CardTitle>
                  </div>
                  <p className="text-[0.6rem] text-muted-foreground">Quality tiers: A = Premium, B = Qualified, C = Nurture</p>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const hl = stats.hot_leads_summary || { tier_a: 0, tier_b: 0, tier_c: 0, total: 0 };
                    const tiers = [
                      { label: "TIER A", sub: "$5K+ · Ready · Full Commit", count: hl.tier_a, color: "hsl(145,50%,45%)", bg: "hsl(145,50%,45%,0.1)", border: "hsl(145,50%,45%,0.25)" },
                      { label: "TIER B", sub: "Qualified · Partial Match", count: hl.tier_b, color: "hsl(40,80%,55%)", bg: "hsl(40,80%,55%,0.1)", border: "hsl(40,80%,55%,0.25)" },
                      { label: "TIER C", sub: "Needs Nurturing", count: hl.tier_c, color: "hsl(240,3%,55%)", bg: "hsl(240,3%,55%,0.1)", border: "hsl(240,3%,55%,0.25)" },
                    ];
                    return (
                      <div className="space-y-3">
                        {tiers.map(t => (
                          <div key={t.label} className="flex items-center gap-3 p-3 rounded-lg border" style={{ backgroundColor: t.bg, borderColor: t.border }}>
                            <div className="w-14 text-center">
                              <p className="text-2xl font-mono font-bold" style={{ color: t.color }}>{t.count}</p>
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: t.color }}>{t.label}</p>
                              <p className="text-[0.6rem] text-muted-foreground">{t.sub}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-mono font-semibold text-foreground">
                                {hl.total > 0 ? Math.round((t.count / hl.total) * 100) : 0}%
                              </p>
                            </div>
                          </div>
                        ))}
                        <div className="pt-2 border-t border-[hsl(240,4%,16%)]">
                          <p className="text-[0.6rem] text-muted-foreground">
                            <span className="font-semibold text-foreground">Tip:</span> Use Tier A for lookalike audience. Tier B for retargeting. Tier C for nurture campaigns.
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* ─── 3) SIGNUP TREND ─── */}
              <Card className="bg-card/70 border-border/70" data-testid="signup-trend">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-[hsl(210,60%,55%)]" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      Signup Trend
                    </CardTitle>
                  </div>
                  <p className="text-[0.6rem] text-muted-foreground">Daily signups & capital inflow. Schedule ads on peak days.</p>
                </CardHeader>
                <CardContent>
                  {stats.signup_trend && stats.signup_trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={stats.signup_trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(350,58%,48%)" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="hsl(350,58%,48%)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,4%,16%)" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 9, fill: "hsl(240,3%,50%)" }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(val) => val ? val.slice(5) : ""}
                        />
                        <YAxis
                          tick={{ fontSize: 9, fill: "hsl(240,3%,50%)" }}
                          tickLine={false}
                          axisLine={false}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{ background: "hsl(240,4%,10%)", border: "1px solid hsl(240,4%,18%)", borderRadius: 8, fontSize: 11 }}
                          labelStyle={{ color: "hsl(30,8%,80%)", fontWeight: 600, marginBottom: 4 }}
                          formatter={(value, name) => [
                            name === "count" ? `${value} signups` : `$${value.toLocaleString()}`,
                            name === "count" ? "Signups" : "Capital"
                          ]}
                        />
                        <Area type="monotone" dataKey="count" stroke="hsl(350,58%,48%)" fill="url(#signupGrad)" strokeWidth={2} dot={{ r: 3, fill: "hsl(350,58%,48%)" }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No trend data yet</p>
                  )}
                  {stats.signup_trend && stats.signup_trend.length > 0 && (
                    <div className="flex gap-4 mt-3 pt-3 border-t border-[hsl(240,4%,16%)]">
                      {stats.signup_trend.map(d => (
                        <div key={d.date} className="text-center">
                          <p className="text-[0.55rem] text-muted-foreground">{d.date.slice(5)}</p>
                          <p className="text-xs font-mono font-bold text-foreground">{d.count}</p>
                          <p className="text-[0.55rem] font-mono text-[hsl(350,58%,48%)]">${d.capital.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ─── 4) REFERRAL × CAPITAL QUALITY ─── */}
              {stats.referral_capital_quality && stats.referral_capital_quality.length > 0 && (
                <Card className="bg-card/70 border-border/70 md:col-span-2" data-testid="referral-capital-quality">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-[hsl(280,50%,55%)]" />
                      <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        Referral × Capital Quality
                      </CardTitle>
                    </div>
                    <p className="text-[0.6rem] text-muted-foreground">Which channels bring high-capital vs low-capital leads? Optimize spend accordingly.</p>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const sources = [...new Set(stats.referral_capital_quality.map(d => d.source))];
                      const capitalTiers = [...new Set(stats.referral_capital_quality.map(d => d.capital_tier))];
                      const chartData = sources.map(src => {
                        const row = { source: src };
                        stats.referral_capital_quality.filter(d => d.source === src).forEach(d => {
                          row[d.capital_tier] = d.count;
                        });
                        return row;
                      });
                      const tierColors = ["hsl(350,58%,48%)", "hsl(350,58%,65%)", "hsl(40,80%,55%)", "hsl(145,50%,45%)"];
                      return (
                        <>
                          <ResponsiveContainer width="100%" height={Math.max(sources.length * 50, 120)}>
                            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }} barSize={18}>
                              <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(240,3%,45%)" }} tickLine={false} axisLine={false} allowDecimals={false} />
                              <YAxis type="category" dataKey="source" tick={{ fontSize: 11, fill: "hsl(240,3%,60%)" }} tickLine={false} axisLine={false} width={120} />
                              <Tooltip
                                contentStyle={{ background: "hsl(240,4%,10%)", border: "1px solid hsl(240,4%,18%)", borderRadius: 8, fontSize: 11 }}
                                cursor={{ fill: "hsla(350,58%,42%,0.06)" }}
                              />
                              <Legend
                                verticalAlign="top"
                                height={32}
                                formatter={(value) => <span className="text-[0.6rem] text-[hsl(240,3%,55%)]">{value}</span>}
                              />
                              {capitalTiers.map((tier, i) => (
                                <Bar key={tier} dataKey={tier} stackId="a" fill={tierColors[i % tierColors.length]} radius={i === capitalTiers.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]} />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* ─── 5) TOP CHALLENGES ─── */}
              {stats.top_challenges && stats.top_challenges.length > 0 && (
                <Card className="bg-card/70 border-border/70 md:col-span-2" data-testid="top-challenges">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <MessageSquareText className="h-4 w-4 text-[hsl(40,80%,55%)]" />
                      <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        Top Challenges — Pain Points for Ad Copy
                      </CardTitle>
                    </div>
                    <p className="text-[0.6rem] text-muted-foreground">Use these exact words in your ads. Speaks directly to audience pain.</p>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-6">
                    <div className="space-y-1.5">
                      {stats.top_challenges.map((item, idx) => {
                        const maxCount = stats.top_challenges[0]?.count || 1;
                        const pct = (item.count / maxCount) * 100;
                        return (
                          <div key={`challenge-${item.challenge}`} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[hsl(240,4%,12%)] transition-colors duration-150">
                            <span className="w-6 text-center font-mono text-xs font-bold text-muted-foreground">#{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="relative h-8 bg-[hsl(240,4%,10%)] rounded overflow-hidden">
                                <div className="absolute inset-y-0 left-0 rounded" style={{
                                  width: `${pct}%`,
                                  background: "linear-gradient(90deg, hsl(40,60%,25%), hsl(40,70%,38%))"
                                }} />
                                <div className="absolute inset-0 flex items-center px-3">
                                  <span className="text-xs text-white/90 truncate">{item.challenge}</span>
                                </div>
                              </div>
                            </div>
                            <Badge variant="outline" className="shrink-0 text-[0.6rem] border-[hsl(240,4%,22%)] bg-[hsl(240,4%,12%)]">
                              {item.count}x
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-3 border-t border-[hsl(240,4%,16%)]">
                      <p className="text-[0.6rem] text-muted-foreground">
                        <span className="font-semibold text-[hsl(40,80%,55%)]">Ad Copy Tip:</span> Mirror these exact phrases in your headlines & hooks. "Struggling with [challenge]? We built something for that."
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </motion.div>
        )}


        {/* Filters */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={2}
          variants={fadeIn}
          className="flex flex-col sm:flex-row gap-3 mb-6"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="admin-search-input"
              placeholder="Search by name, Telegram, or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 bg-background/40 border-border/80 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-0"
            />
          </div>
          <Select value={capitalFilter} onValueChange={setCapitalFilter}>
            <SelectTrigger
              className="w-full sm:w-[180px] bg-background/40 border-border/80"
              data-testid="admin-filter-capital-select"
            >
              <SelectValue placeholder="Capital" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Capital</SelectItem>
              {CAPITAL_FILTERS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={experienceFilter} onValueChange={setExperienceFilter}>
            <SelectTrigger
              className="w-full sm:w-[160px] bg-background/40 border-border/80"
              data-testid="admin-filter-experience-select"
            >
              <SelectValue placeholder="Experience" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Experience</SelectItem>
              {EXPERIENCE_FILTERS.map((e) => (
                <SelectItem key={e} value={e}>{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={readinessFilter} onValueChange={setReadinessFilter}>
            <SelectTrigger
              className="w-full sm:w-[180px] bg-background/40 border-border/80"
              data-testid="admin-filter-readiness-select"
            >
              <SelectValue placeholder="Readiness" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Readiness</SelectItem>
              {READINESS_FILTERS.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Table */}
        <motion.div initial="hidden" animate="visible" custom={3} variants={fadeIn}>
          <Card className="bg-card/70 border-border/70 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-3 text-sm text-muted-foreground">Loading applications...</span>
              </div>
            ) : applications.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-16 px-4"
                data-testid="admin-empty-state"
              >
                <Users className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No submissions yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Applications will appear here once traders submit the form.
                </p>
              </div>
            ) : (
              <ScrollArea className="w-full">
                <Table className="text-sm" data-testid="admin-responses-table">
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Name
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">
                        Contact
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Capital
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">
                        Duration
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell">
                        Readiness
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">
                        Date
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[40px]">
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRowExpand
                        key={app.id}
                        app={app}
                        expanded={expandedRow === app.id}
                        onToggle={() =>
                          setExpandedRow(expandedRow === app.id ? null : app.id)
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </Card>
        </motion.div>

        {/* Count */}
        {!loading && applications.length > 0 && (
          <motion.p
            initial="hidden"
            animate="visible"
            custom={4}
            variants={fadeIn}
            className="text-xs text-muted-foreground mt-3"
          >
            Showing {applications.length} application{applications.length !== 1 ? "s" : ""}
          </motion.p>
        )}

        {/* Email Blast */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={5}
          variants={fadeIn}
          className="mt-8"
        >
          <EmailBlastSection totalEmails={applications.filter(a => a.email).length} />
        </motion.div>
      </div>
    </div>
  );
}

function EmailBlastSection({ totalEmails }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${process.env.REACT_APP_ADMIN_EMAIL_TOKEN || ""}` } };

  const handleTestEmail = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/email/test`, {
        subject,
        html_content: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;background:#111;color:#eee;border-radius:8px;"><h2 style="color:#a3344a;margin:0 0 16px;">${subject}</h2><p style="line-height:1.7;color:#ccc;">${body.replace(/\n/g, "<br/>")}</p><hr style="border:none;border-top:1px solid #222;margin:20px 0;"/><p style="font-size:11px;color:#555;">The Last Dance Capital</p></div>`,
      }, authHeaders);
      setResult({ type: "success", msg: `Test email sent! ID: ${res.data.email_id}` });
    } catch (err) {
      setResult({ type: "error", msg: err?.response?.data?.detail || "Failed to send test email" });
    } finally {
      setSending(false);
    }
  };

  const handleBlast = async () => {
    if (!subject.trim() || !body.trim()) return;
    if (!window.confirm(`Send this email to ${totalEmails} applicant(s)? Duplicate emails will be filtered.`)) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/email/blast`, {
        subject,
        html_content: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;background:#111;color:#eee;border-radius:8px;"><h2 style="color:#a3344a;margin:0 0 16px;">${subject}</h2><p style="line-height:1.7;color:#ccc;">Hi {{name}},</p><p style="line-height:1.7;color:#ccc;">${body.replace(/\n/g, "<br/>")}</p><hr style="border:none;border-top:1px solid #222;margin:20px 0;"/><p style="font-size:11px;color:#555;">The Last Dance Capital</p></div>`,
      }, authHeaders);
      setResult({
        type: "success",
        msg: `Blast complete: ${res.data.sent} sent, ${res.data.failed} failed out of ${res.data.total_recipients}`,
      });
    } catch (err) {
      setResult({ type: "error", msg: err?.response?.data?.detail || "Blast failed" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-card/70 border-border/70">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-[hsl(350,58%,48%)]" />
          Email Blast
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Send email to all applicants. Use <code className="text-[hsl(350,58%,48%)]">{"{{name}}"}</code> for personalization.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1.5 block">
            Subject
          </label>
          <Input
            placeholder="e.g. The Last Dance is Ready"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="bg-background/40 border-border/80"
            data-testid="email-subject-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1.5 block">
            Message Body
          </label>
          <Textarea
            placeholder="Write your message here..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="min-h-[120px] bg-background/40 border-border/80"
            data-testid="email-body-input"
          />
        </div>

        {result && (
          <div className={`text-xs p-3 rounded-lg ${result.type === "success" ? "bg-[hsl(240,4%,14%)] text-[hsl(30,8%,80%)]" : "bg-[hsl(350,58%,42%,0.1)] text-[hsl(350,58%,55%)]"}`}>
            {result.msg}
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleTestEmail}
            disabled={sending || !subject.trim() || !body.trim()}
            className="rounded-xl bg-secondary/70 hover:bg-secondary"
            data-testid="email-test-button"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Send Test
          </Button>
          <Button
            onClick={handleBlast}
            disabled={sending || !subject.trim() || !body.trim() || totalEmails === 0}
            className="rounded-xl bg-[hsl(350,58%,42%)] hover:bg-[hsl(350,58%,38%)] text-white"
            data-testid="email-blast-button"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Blast to {totalEmails} emails
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TableRowExpand({ app, expanded, onToggle }) {
  return (
    <>
      <TableRow
        className="border-border/30 table-row-hover cursor-pointer"
        onClick={onToggle}
        data-testid={`admin-row-${app.id}`}
      >
        <TableCell className="font-medium text-foreground">
          <div>
            {app.full_name}
            {app.country && <p className="text-xs text-muted-foreground font-normal mt-0.5">{app.country}</p>}
            <p className="text-xs text-muted-foreground font-mono mt-0.5 sm:hidden">{app.telegram_username}</p>
          </div>
        </TableCell>
        <TableCell className="hidden sm:table-cell">
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground font-mono">{app.telegram_username}</p>
            <p className="text-xs text-muted-foreground font-mono">{app.email}</p>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-xs whitespace-nowrap ${capitalBadgeColor(app.trading_capital)}`}>
            {shortLabel(app.trading_capital)}
          </Badge>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <Badge variant="outline" className={`text-xs whitespace-nowrap ${experienceBadgeColor(app.trading_experience)}`}>
            {shortLabel(app.trading_experience)}
          </Badge>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap">{shortLabel(app.readiness_to_start)}</TableCell>
        <TableCell className="text-xs text-muted-foreground font-mono hidden sm:table-cell whitespace-nowrap">
          {formatDate(app.created_at)}
        </TableCell>
        <TableCell>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="border-border/20 bg-background/20">
          <TableCell colSpan={7} className="py-4 px-4 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {/* Show contact on mobile (hidden in table) */}
              <div className="sm:hidden">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Contact
                </p>
                <p className="text-sm text-foreground font-mono">{app.email || "—"}</p>
              </div>
              <div className="md:hidden">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Duration
                </p>
                <p className="text-sm text-foreground">{app.trading_experience || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Copytrade Experience
                </p>
                <p className="text-sm text-foreground">{app.signals_experience || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Referral Source
                </p>
                <p className="text-sm text-foreground">{app.referral_source || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Investment Target
                </p>
                <p className="text-sm text-foreground font-mono">
                  {app.desired_capital ? `$${Number(app.desired_capital).toLocaleString()} @ ${app.daily_target_percent}%/day` : "—"}
                </p>
                {app.desired_capital > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2 text-xs font-mono">
                    <span className="text-muted-foreground">Daily (5%) <span className="text-foreground font-semibold">${(app.desired_capital * 0.05).toFixed(0)}</span></span>
                    <span className="text-muted-foreground">Daily (10%) <span className="text-foreground font-semibold">${(app.desired_capital * 0.10).toFixed(0)}</span></span>
                    <span className="text-muted-foreground">Monthly <span className="text-[hsl(350,58%,48%)] font-semibold">${(app.desired_capital * 0.05 * 22).toLocaleString()} - ${(app.desired_capital * 0.10 * 22).toLocaleString()}</span></span>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Commitment
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className={`text-[0.65rem] ${app.commitment_open_ib ? "bg-[hsl(240,4%,16%)] text-[hsl(30,8%,80%)] border-[hsl(240,4%,22%)]" : "bg-[hsl(350,58%,42%,0.12)] text-[hsl(350,58%,52%)] border-[hsl(350,58%,42%,0.25)]"}`}>
                    IB: {app.commitment_open_ib ? "Yes" : "No"}
                  </Badge>
                  <Badge variant="outline" className={`text-[0.65rem] ${app.commitment_risk_rules ? "bg-[hsl(240,4%,16%)] text-[hsl(30,8%,80%)] border-[hsl(240,4%,22%)]" : "bg-[hsl(350,58%,42%,0.12)] text-[hsl(350,58%,52%)] border-[hsl(350,58%,42%,0.25)]"}`}>
                    Rules: {app.commitment_risk_rules ? "Yes" : "No"}
                  </Badge>
                  <Badge variant="outline" className={`text-[0.65rem] ${app.commitment_one_month ? "bg-[hsl(240,4%,16%)] text-[hsl(30,8%,80%)] border-[hsl(240,4%,22%)]" : "bg-[hsl(350,58%,42%,0.12)] text-[hsl(350,58%,52%)] border-[hsl(350,58%,42%,0.25)]"}`}>
                    1M: {app.commitment_one_month ? "Yes" : "No"}
                  </Badge>
                  <Badge variant="outline" className={`text-[0.65rem] ${app.accept_ai_tools ? "bg-[hsl(240,4%,16%)] text-[hsl(30,8%,80%)] border-[hsl(240,4%,22%)]" : "bg-[hsl(350,58%,42%,0.12)] text-[hsl(350,58%,52%)] border-[hsl(350,58%,42%,0.25)]"}`}>
                    AI: {app.accept_ai_tools ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
              <div className="sm:col-span-2 md:col-span-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Why They Want In
                </p>
                <p className="text-sm text-foreground/85 leading-relaxed">
                  {app.biggest_challenge || "—"}
                </p>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
